import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, User, Mail, Phone, Palette, Star, Plus, X, AlertTriangle, Store, Trash2, RotateCcw } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { ConfirmInvitationModal } from '../components/ConfirmInvitationModal';
import { SuccessModal } from '../components/SuccessModal';
import { apiClient } from '../utils/api';
import { formatPhoneAsYouType, parseInternationalPhone, type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { TimeInput } from '../components/TimeInput';
interface TimeSlot {
  open: string;
  close: string;
}

type ScheduleDay = TimeSlot[];

interface WeeklySchedule {
  monday?: ScheduleDay;
  tuesday?: ScheduleDay;
  wednesday?: ScheduleDay;
  thursday?: ScheduleDay;
  friday?: ScheduleDay;
  saturday?: ScheduleDay;
  sunday?: ScheduleDay;
}

type DayKey = keyof WeeklySchedule;

const planningDays: Array<{ key: DayKey; label: string }> = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

const normalizeTimeSlots = (value: unknown): TimeSlot[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter((slot): slot is TimeSlot => (
        !!slot &&
        typeof slot === 'object' &&
        typeof (slot as TimeSlot).open === 'string' &&
        typeof (slot as TimeSlot).close === 'string'
      ))
      .map((slot) => ({ open: slot.open, close: slot.close }));
  }

  if (
    typeof value === 'object' &&
    typeof (value as TimeSlot).open === 'string' &&
    typeof (value as TimeSlot).close === 'string'
  ) {
    const slot = value as TimeSlot;
    return [{ open: slot.open, close: slot.close }];
  }

  return [];
};

const normalizeWeeklySchedule = (schedule: unknown): WeeklySchedule => {
  if (!schedule || typeof schedule !== 'object') return {};

  const raw = schedule as Record<string, unknown>;
  const normalized: WeeklySchedule = {};

  planningDays.forEach(({ key }) => {
    const slots = normalizeTimeSlots(raw[key]);
    if (slots.length > 0) {
      normalized[key] = slots;
    }
  });

  return normalized;
};

const normalizeShopSchedules = (value: unknown): Record<string, WeeklySchedule> => {
  if (!value || typeof value !== 'object') return {};

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, WeeklySchedule>>(
    (acc, [shopId, schedule]) => {
      acc[shopId] = normalizeWeeklySchedule(schedule);
      return acc;
    },
    {},
  );
};

interface PractitionerPlanningProps {
  value: {
    schedule: WeeklySchedule;
  };
  onChange: (planning: { schedule: WeeklySchedule }) => void;
}

function PractitionerPlanning({ value, onChange }: PractitionerPlanningProps) {
  const updateSchedule = (schedule: WeeklySchedule) => {
    onChange({ schedule });
  };

  const addSlot = (day: DayKey) => {
    const currentSlots = value.schedule[day] || [];
    const defaultSlot: TimeSlot = currentSlots.length === 0
      ? { open: '09:00', close: '12:00' }
      : { open: '14:00', close: '18:00' };

    updateSchedule({
      ...value.schedule,
      [day]: [...currentSlots, defaultSlot],
    });
  };

  const updateSlot = (
    day: DayKey,
    index: number,
    field: keyof TimeSlot,
    nextValue: string,
  ) => {
    const currentSlots = value.schedule[day] || [];
    const nextSlots = currentSlots.map((slot, slotIndex) => (
      slotIndex === index
        ? { ...slot, [field]: nextValue }
        : slot
    ));

    updateSchedule({
      ...value.schedule,
      [day]: nextSlots,
    });
  };

  const removeSlot = (day: DayKey, index: number) => {
    const currentSlots = value.schedule[day] || [];
    const nextSlots = currentSlots.filter((_, slotIndex) => slotIndex !== index);
    const nextSchedule = { ...value.schedule };

    if (nextSlots.length === 0) {
      delete nextSchedule[day];
    } else {
      nextSchedule[day] = nextSlots;
    }

    updateSchedule(nextSchedule);
  };

  return (
    <div className="card">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-textPrimary">Disponibilités hebdomadaires</h3>
        <p className="mt-1 text-sm text-textSecondary">
          Ajoutez un ou plusieurs créneaux pour chaque jour.
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {planningDays.map(({ key, label }) => {
          const slots = value.schedule[key] || [];

          return (
            <div key={key} className="py-5 first:pt-0 last:pb-0">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[140px_1fr]">
                <div>
                  <div className="font-medium text-textPrimary">{label}</div>
                  {slots.length === 0 && (
                    <div className="mt-1 text-sm text-gray-500">Indisponible</div>
                  )}
                </div>

                <div className="space-y-3">
                  {slots.map((slot, index) => (
                    <div
                      key={`${key}-${index}`}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-end"
                    >
                      <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600">De</label>
                        <TimeInput
                          value={slot.open}
                          onChange={(value) => updateSlot(key, index, 'open', value)}
                          className="input-field w-full"
                        />
                      </div>

                      <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600">À</label>
                        <TimeInput
                          value={slot.close}
                          onChange={(value) => updateSlot(key, index, 'close', value)}
                          className="input-field w-full"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeSlot(key, index)}
                        className="inline-flex h-10 items-center justify-center rounded-lg border border-red-200 bg-white px-3 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                        title="Supprimer ce créneau"
                        aria-label={`Supprimer le créneau ${index + 1} du ${label.toLowerCase()}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => addSlot(key)}
                    className="inline-flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter un créneau à ce jour
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PractitionerFormData {
  id: string;
  firstName: string;
  lastName: string;
  artistName: string;
  code: string;
  email: string;
  phone: string;
  bio: string;
  experience: number;
  color?: string; // Calendar color (hex format)
  specialties: Array<{
    category: 'TATTOO' | 'PIERCING';
    styles: string[];
    experience: number;
    certifications: string[];
  }>;
  certifications: Array<{
    name: string;
    issuer: string;
    number: string;
    issuedAt: string;
    expiresAt?: string;
    verified: boolean;
  }>;
  availability: {
    // Ancien planning global conserve pour assurer la compatibilite
    // avec les donnees existantes pendant la migration.
    schedule: WeeklySchedule;
    // Nouveau : un planning independant pour chaque shop.
    shopSchedules: Record<string, WeeklySchedule>;
    bookingWindow: number;
    minNotice: number;
  };
  pricing: {
    level: 'junior' | 'senior' | 'master' | 'guest';
    hourlyRate: number;
    minimumCharge: number;
    consultationFee: number;
  };
  rating: number;
  reviewCount: number;
  isActive: boolean;
  onlineBookingEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  userId?: string | null;
}

interface ShopAssociation {
  id: string;
  shopId: string;
  shopName: string;
  shopCity: string;
  relationshipType: 'FREELANCE' | 'EMPLOYEE' | 'RENTAL' | 'PARTNERSHIP';
  hourlyRate?: number;
  commissionRate?: number;
  isActive: boolean;
  startDate: string;
  endDate?: string;
}

interface Shop {
  id: string;
  name: string;
  address: {
    city: string;
  };
}

interface ServiceOffering {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  basePrice: number;
  estimatedDuration: number;
  onlineBookingEnabled: boolean;
  isOffered: boolean;
  isAvailable: boolean;
  customPrice?: number;
  specialNotes?: string;
}

const tattooStyles = [
  'Traditional', 'Realism', 'Watercolor', 'Geometric', 'Minimalist',
  'Blackwork', 'Dotwork', 'Tribal', 'Japanese', 'Biomechanical'
];

const piercingTypes = [
  'Ear Lobe', 'Ear Cartilage', 'Nose', 'Eyebrow', 'Lip',
  'Tongue', 'Navel', 'Nipple', 'Surface', 'Dermal'
];

export function PractitionerEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [shopToRemove, setShopToRemove] = useState<ShopAssociation | null>(null);
  const [practitioner, setPractitioner] = useState<PractitionerFormData | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [associatedShops, setAssociatedShops] = useState<ShopAssociation[]>([]);
  const [serviceOfferings, setServiceOfferings] = useState<ServiceOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAddShopModal, setShowAddShopModal] = useState(false);
  const [invitationModal, setInvitationModal] = useState<'send' | 'link' | null>(null);
  const [invitationSending, setInvitationSending] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null);
  const [selectedShopId, setSelectedShopId] = useState<string>('');
  const [selectedPlanningShopId, setSelectedPlanningShopId] = useState<string>('');
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [newAssociation, setNewAssociation] = useState({
    relationshipType: 'FREELANCE' as const,
    hourlyRate: '',
    commissionRate: '',
  });

  useEffect(() => {
    if (id) {
      fetchPractitioner(id);
      fetchShops();
      fetchAssociatedShops(id);
      fetchPractitionerServices(id);
    }
  }, [id]);

  useEffect(() => {
    const activeShops = associatedShops.filter((association) => association.isActive);

    if (activeShops.length === 0) {
      setSelectedPlanningShopId('');
      return;
    }

    const selectedShopIsStillActive = activeShops.some(
      (association) => association.shopId === selectedPlanningShopId,
    );

    if (!selectedShopIsStillActive) {
      setSelectedPlanningShopId(activeShops[0].shopId);
    }
  }, [associatedShops, selectedPlanningShopId]);

  // Si le praticien possede encore uniquement l'ancien planning global,
  // on le rattache au premier shop actif afin de ne pas perdre ses horaires.
  useEffect(() => {
    if (!practitioner) return;

    const activeShops = associatedShops.filter((association) => association.isActive);
    if (activeShops.length === 0) return;

    const shopSchedules = practitioner.availability.shopSchedules || {};
    const legacySchedule = practitioner.availability.schedule || {};

    if (Object.keys(shopSchedules).length > 0 || Object.keys(legacySchedule).length === 0) {
      return;
    }

    const firstShopId = activeShops[0].shopId;

    setPractitioner((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        availability: {
          ...prev.availability,
          shopSchedules: {
            ...prev.availability.shopSchedules,
            [firstShopId]: prev.availability.schedule,
          },
        },
      };
    });
  }, [associatedShops, practitioner]);

  const fetchPractitioner = async (practitionerId: string) => {
    try {
      setLoading(true);
      const response = await apiClient(`/api/admin/practitioners/${practitionerId}`);

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.message || 'Failed to fetch practitioner');
      }

      const data = await response.json();
      if (data.success) {
        const practitionerData = data.data.practitioner;
        const rawPhone = practitionerData.phone || '';
        const parsedPhone = parseInternationalPhone(rawPhone, 'FR');
        const nextPhoneCountry = parsedPhone.country ?? 'FR';
        const nextPhoneDisplay = formatPhoneAsYouType(rawPhone, nextPhoneCountry);
        setPhoneCountry(nextPhoneCountry);
        setPhoneDisplay(nextPhoneDisplay);
        setPhoneE164(parsedPhone.isValid ? parsedPhone.e164 : null);
        setPhoneIsValid(parsedPhone.isValid);
        // Ensure arrays and nested objects are properly initialized
        setPractitioner({
          ...practitionerData,
          phone: nextPhoneDisplay,
          artistName: practitionerData.artistName || '',
          code: practitionerData.code || '',
          specialties: Array.isArray(practitionerData.specialties) 
            ? practitionerData.specialties.map((specialty: any) => ({
                category: specialty.category || 'TATTOO',
                styles: Array.isArray(specialty.styles) ? specialty.styles : [],
                experience: specialty.experience || 0,
                certifications: Array.isArray(specialty.certifications) ? specialty.certifications : []
              }))
            : [],
          certifications: Array.isArray(practitionerData.certifications) 
            ? practitionerData.certifications 
            : [],
          availability: {
            schedule: normalizeWeeklySchedule(practitionerData.availability?.schedule),
            shopSchedules: normalizeShopSchedules(practitionerData.availability?.shopSchedules),
            bookingWindow: practitionerData.availability?.bookingWindow || 90,
            minNotice: practitionerData.availability?.minNotice || 24,
          },
          pricing: practitionerData.pricing || {
            level: 'junior',
            hourlyRate: 0,
            minimumCharge: 0,
            consultationFee: 0
          },
          onlineBookingEnabled: practitionerData.onlineBookingEnabled !== undefined 
            ? practitionerData.onlineBookingEnabled 
            : false
        });
      } else {
        setError(data.message || 'Failed to load practitioner');
      }
    } catch (error) {
      console.error('Fetch practitioner error:', error);
      setError(error instanceof Error ? error.message : 'Failed to load practitioner data');
    } finally {
      setLoading(false);
    }
  };

  const fetchShops = async () => {
    try {
      // Association management needs the complete set of shops the admin can
      // access, independently of the shop currently selected in the header.
      // Otherwise the API returns only the current shop, which is commonly
      // already associated and therefore filtered out of the select below.
      const response = await apiClient('/api/admin/shops?limit=1000&allShops=true');

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShops(Array.isArray(data.data?.shops) ? data.data.shops : []);
        }
      }
    } catch (error) {
      console.error('Fetch shops error:', error);
    }
  };

  const fetchAssociatedShops = async (practitionerId: string) => {
    try {
      const response = await apiClient(`/api/admin/practitioners/${practitionerId}/shops`);

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAssociatedShops(data.data.shops || []);
        }
      }
    } catch (error) {
      console.error('Fetch associated shops error:', error);
    }
  };

  const fetchPractitionerServices = async (practitionerId: string) => {
    try {
      const response = await apiClient(`/api/admin/practitioners/${practitionerId}/services`);

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setServiceOfferings(data.data.services || []);
        }
      }
    } catch (error) {
      console.error('Fetch practitioner services error:', error);
    }
  };

  const handleServiceChange = (serviceId: string, field: string, value: any) => {
    setServiceOfferings(prev => 
      prev.map(service => 
        service.id === serviceId 
          ? { ...service, [field]: value }
          : service
      )
    );
    setHasUnsavedChanges(true);
  };

  const handleAddShopAssociation = async () => {
    if (!selectedShopId || !id) return;

    try {
      const response = await apiClient(`/api/planning/practitioners/${id}/shops/${selectedShopId}`, {
        method: 'POST',
        body: JSON.stringify({
          relationshipType: newAssociation.relationshipType,
          hourlyRate: newAssociation.hourlyRate ? parseFloat(newAssociation.hourlyRate) : null,
          commissionRate: newAssociation.commissionRate ? parseFloat(newAssociation.commissionRate) : null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh associated shops
          await fetchAssociatedShops(id);
          setShowAddShopModal(false);
          setSelectedShopId('');
          setNewAssociation({
            relationshipType: 'FREELANCE',
            hourlyRate: '',
            commissionRate: '',
          });
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add shop association');
      }
    } catch (error) {
      console.error('Add shop association error:', error);
      setError('Failed to add shop association');
    }
  };

  const handleReactivateShopAssociation = async (association: ShopAssociation) => {
    if (!id) return;
  
    try {
      setError(null);
  
      const response = await apiClient(
        `/api/planning/practitioners/${id}/shops/${association.shopId}`,
        {
          method: 'POST',
          body: JSON.stringify({
            relationshipType: association.relationshipType,
            hourlyRate: association.hourlyRate ?? null,
            commissionRate: association.commissionRate ?? null,
          }),
        }
      );
  
      const data = await response.json().catch(() => ({}));
  
      if (!response.ok || !data.success) {
        setError(data.message || 'Impossible de réactiver ce shop');
        return;
      }
  
      await fetchAssociatedShops(id);
    } catch (error) {
      console.error('Reactivate shop association error:', error);
      setError('Erreur lors de la réactivation du shop');
    }
  };

  const handleAskRemoveShopAssociation = (association: ShopAssociation) => {
    const activeAssociations = associatedShops.filter(
      (item) => item.isActive
    );
  
    if (association.isActive && activeAssociations.length <= 1) {
      setError('Un praticien doit être associé à au moins un shop actif.');
      return;
    }
  
    setShopToRemove(association);
  };

  const handleConfirmRemoveShopAssociation = async () => {
  if (!id || !shopToRemove) return;

  try {
    setError(null);

    const response = await apiClient(
      `/api/planning/practitioners/${id}/shops/${shopToRemove.shopId}`,
      {
        method: 'DELETE',
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      setError(data.message || 'Impossible de retirer ce shop');
      return;
    }

    await fetchAssociatedShops(id);
    setShopToRemove(null);
  } catch (error) {
    console.error('Remove shop association error:', error);
    setError('Erreur lors du retrait du shop');
  }
};

  const handleInputChange = (field: string, value: any) => {
    if (!practitioner) return;
    
    const keys = field.split('.');
    setPractitioner(prev => {
      if (!prev) return prev;
      
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    
    setHasUnsavedChanges(true);
  };

  const addSpecialty = () => {
    if (!practitioner) return;
    
    setPractitioner(prev => prev ? {
      ...prev,
      specialties: [
        ...prev.specialties,
        {
          category: 'TATTOO',
          styles: [],
          experience: 0,
          certifications: []
        }
      ]
    } : prev);
    
    setHasUnsavedChanges(true);
  };

  const removeSpecialty = (index: number) => {
    if (!practitioner) return;
    
    setPractitioner(prev => prev ? {
      ...prev,
      specialties: prev.specialties.filter((_, i) => i !== index)
    } : prev);
    
    setHasUnsavedChanges(true);
  };

  const updateSpecialty = (index: number, field: string, value: any) => {
    if (!practitioner) return;
    
    setPractitioner(prev => prev ? {
      ...prev,
      specialties: prev.specialties.map((specialty, i) => 
        i === index ? { ...specialty, [field]: value } : specialty
      )
    } : prev);
    
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!practitioner) return;
    if (!phoneDisplay.trim() || !phoneIsValid || !phoneE164) {
      setError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);
      
      const response = await apiClient(`/api/admin/practitioners/${practitioner.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...practitioner, code: practitioner.code.trim()
    ? practitioner.code.trim().toUpperCase()
    : null, phone: phoneE164 }),
      });

      const data = await response.json();
      if (!data.success) {
        setError(data.message || 'Échec de la sauvegarde du praticien');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
        return;
      }

      // Save practitioner services
      if (id) {
        const servicesResponse = await apiClient(`/api/admin/practitioners/${id}/services`, {
          method: 'PUT',
          body: JSON.stringify({
            services: serviceOfferings.map(service => ({
              serviceId: service.id,
              isOffered: service.isOffered,
              isAvailable: service.isAvailable,
              customPrice: service.customPrice,
              specialNotes: service.specialNotes
            }))
          }),
        });

        const servicesData = await servicesResponse.json();
        if (!servicesData.success) {
          setError(servicesData.message || 'Échec de la sauvegarde des services');
          if (Array.isArray(servicesData.errors) && servicesData.errors.length > 0) {
            setErrorDetails(servicesData.errors);
          }
          return;
        }
      }

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Save practitioner error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?')) {
        navigate('/practitioners');
      }
    } else {
      navigate('/practitioners');
    }
  };

  const handleInvitationConfirm = async () => {
    if (!practitioner) return;
    setInvitationSending(true);
    setInvitationError(null);
    try {
      const endpoint =
        invitationModal === 'link'
          ? `/api/admin/practitioners/${practitioner.id}/link-account`
          : `/api/admin/practitioners/${practitioner.id}/send-invitation`;
      const resp = await apiClient(endpoint, { method: 'POST' });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || t('common.error'));
      }
      setInvitationModal(null);
      const isLink = invitationModal === 'link';
      setSuccessModal({
        title: isLink ? t('practitionerInvitation.successModal.titleCreated') : t('practitionerInvitation.successModal.titleSent'),
        message: isLink ? t('practitionerInvitation.successModal.messageCreated') : t('practitionerInvitation.successModal.messageSent'),
      });
      if (invitationModal === 'link' && id) {
        fetchPractitioner(id);
      }
    } catch (e: any) {
      setInvitationError(e?.message || t('practitionerInvitation.error'));
    } finally {
      setInvitationSending(false);
    }
  };

  const closeInvitationModal = () => {
    if (!invitationSending) {
      setInvitationModal(null);
      setInvitationError(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement de l'artiste...</span>
      </div>
    );
  }

  if (!practitioner) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || t('navigation.practitioners.notFound')}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate('/practitioners')}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des artistes
        </button>
      </div>
    );
  }

  const activeAssociatedShops = associatedShops.filter(
    (association) => association.isActive,
  );

  const selectedPlanningShop = activeAssociatedShops.find(
    (association) => association.shopId === selectedPlanningShopId,
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Modifier: {practitioner.firstName} {practitioner.lastName}</span>
              <HelpIcon 
                docPath="06-gestion-praticiens#06-5-modification" 
                tooltip="Documentation : Modification d'un praticien"
              />
            </h1>
            {hasUnsavedChanges && (
              <p className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded mt-1">
                Modifications non sauvegardées
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Account / Login */}
      <div className="card mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-textPrimary flex items-center space-x-2">
              <User className="w-5 h-5 text-primary-600" />
              <span>{t('practitionerAccount.loginAccount')}</span>
            </h2>
            <p className="text-sm text-textSecondary mt-1">
              {practitioner.userId
                ? t('practitionerAccount.canLogin')
                : t('practitionerAccount.noAccountCreated')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!practitioner.userId ? (
              <button
                type="button"
                onClick={() => {
                  setInvitationError(null);
                  setInvitationModal('link');
                }}
                className="btn-primary flex items-center space-x-2"
              >
                <User className="w-4 h-4" />
                <span>{t('practitionerAccount.createAccount')}</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setInvitationError(null);
                    setInvitationModal('send');
                  }}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>{t('practitionerAccount.sendInvitation')}</span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const p1 = window.prompt(t('practitionerAccount.setPasswordPrompt'), '');
                    if (!p1) return;
                    const p2 = window.prompt(t('practitionerAccount.confirmPasswordPrompt'), '');
                    if (!p2 || p1 !== p2) {
                      window.alert(t('practitionerAccount.passwordsMismatch'));
                      return;
                    }
                    try {
                      const resp = await apiClient(`/api/admin/practitioners/${practitioner.id}/password`, {
                        method: 'PUT',
                        body: JSON.stringify({ password: p1 }),
                      });
                      const data = await resp.json().catch(() => ({}));
                      if (!resp.ok || !data?.success) throw new Error(data?.message || t('practitionerAccount.passwordUpdateError'));
                      window.alert(t('practitionerAccount.passwordUpdated'));
                    } catch (e: any) {
                      window.alert(e?.message || t('practitionerAccount.passwordUpdateErrorGeneric'));
                    }
                  }}
                  className="btn-secondary"
                >
                  {t('practitionerAccount.setPassword')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-8">
        {/* Personal Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <User className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations Personnelles</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Row 1: Prénom, Nom, Téléphone */}
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Prénom *
              </label>
              <input
                type="text"
                value={practitioner.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom *
              </label>
              <input
                type="text"
                value={practitioner.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Star className="w-4 h-4 inline mr-1" />
                Nom d'artiste
              </label>
              <input
                type="text"
                value={practitioner.artistName}
                onChange={(e) => handleInputChange('artistName', e.target.value)}
                className="input-field"
                placeholder="Nom d'artiste (affiché aux clients)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Code
              </label>
              <input
                type="text"
                value={practitioner.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                className="input-field uppercase"
                maxLength={4}
                placeholder="JD01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Téléphone *
              </label>
              <InternationalPhoneInput
                value={phoneDisplay}
                country={phoneCountry}
                required
                onChange={(result) => {
                  setPhoneDisplay(result.displayValue);
                  setPhoneCountry(result.country);
                  setPhoneE164(result.e164Value);
                  setPhoneIsValid(result.isValid);
                  handleInputChange('phone', result.displayValue);
                  if (result.isValid) {
                    setError((prev) =>
                      prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                    );
                  }
                }}
                selectClassName="input-field"
                inputClassName="input-field"
              />
            </div>

            {/* Row 2: Email */}
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </label>
              <input
                type="email"
                autoComplete="off"
                value={practitioner.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-sm font-medium text-textPrimary mb-2 flex items-center">
                <Palette className="w-4 h-4 mr-1" />
                Couleur du calendrier
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="color"
                  value={practitioner.color || '#3B82F6'}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={practitioner.color || ''}
                  onChange={(e) => {
                    const color = e.target.value;
                    if (color === '' || /^#[0-9A-F]{6}$/i.test(color)) {
                      handleInputChange('color', color || undefined);
                    }
                  }}
                  placeholder="#3B82F6"
                  className="input-field flex-1 max-w-xs"
                  pattern="^#[0-9A-F]{6}$"
                />
                <div className="text-xs text-gray-500">
                  Couleur utilisée dans le calendrier pour identifier ce praticien
                </div>
              </div>
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Biographie
              </label>
              <textarea
                value={practitioner.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={3}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Années d'expérience *
              </label>
              <input
                type="number"
                min="0"
                value={practitioner.experience}
                onChange={(e) => handleInputChange('experience', parseInt(e.target.value) || 0)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Statut
              </label>
              <select
                value={practitioner.isActive ? 'active' : 'inactive'}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                className="input-field"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>

            <div className="flex items-end pb-1">
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={practitioner.onlineBookingEnabled || false}
                    onChange={(e) => handleInputChange('onlineBookingEnabled', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-textPrimary">Réservation en ligne activée</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-7">
                  Permet aux clients de réserver directement en ligne
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
              <Star className="w-5 h-5 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Statistiques</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{practitioner.rating.toFixed(1)}</div>
              <div className="text-sm text-gray-600">Note moyenne</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{practitioner.reviewCount}</div>
              <div className="text-sm text-gray-600">Avis clients</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{practitioner.experience}</div>
              <div className="text-sm text-gray-600">Années d'expérience</div>
            </div>
          </div>
        </div>

        {/* Specialties */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                <Palette className="w-5 h-5 text-accent-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Spécialités</h2>
            </div>
            <button
              type="button"
              onClick={addSpecialty}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter une spécialité</span>
            </button>
          </div>

          {practitioner.specialties.map((specialty, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-textPrimary">Spécialité {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeSpecialty(index)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Catégorie
                  </label>
                  <select
                    value={specialty.category}
                    onChange={(e) => updateSpecialty(index, 'category', e.target.value)}
                    className="input-field"
                  >
                    <option value="TATTOO">Tatouage</option>
                    <option value="PIERCING">Piercing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Expérience (années)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={specialty.experience}
                    onChange={(e) => updateSpecialty(index, 'experience', parseInt(e.target.value) || 0)}
                    className="input-field"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Styles
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {(specialty.category === 'TATTOO' ? tattooStyles : piercingTypes).map((style) => (
                      <label key={style} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={specialty.styles.includes(style)}
                          onChange={(e) => {
                            const newStyles = e.target.checked
                              ? [...specialty.styles, style]
                              : specialty.styles.filter(s => s !== style);
                            updateSpecialty(index, 'styles', newStyles);
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-textPrimary">{style}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {practitioner.specialties.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Aucune spécialité ajoutée</p>
              <p className="text-sm">Cliquez sur "Ajouter une spécialité" pour commencer</p>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <Star className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Tarification</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Niveau
              </label>
              <select
                value={practitioner.pricing.level}
                onChange={(e) => handleInputChange('pricing.level', e.target.value)}
                className="input-field"
              >
                <option value="junior">Junior</option>
                <option value="senior">Senior</option>
                <option value="master">Master</option>
                <option value="guest">Invité</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Tarif horaire (€)
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={practitioner.pricing.hourlyRate}
                onChange={(e) => handleInputChange('pricing.hourlyRate', parseFloat(e.target.value) || 0)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Tarif minimum (€)
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={practitioner.pricing.minimumCharge}
                onChange={(e) => handleInputChange('pricing.minimumCharge', parseFloat(e.target.value) || 0)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Consultation (€)s
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={practitioner.pricing.consultationFee}
                onChange={(e) => handleInputChange('pricing.consultationFee', parseFloat(e.target.value) || 0)}
                className="input-field"
              />
            </div>
          </div>
        </div>


        {/* Planning par shop */}
        {selectedPlanningShop ? (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-textPrimary">
                  Planning de {selectedPlanningShop.shopName}
                </h2>
                {selectedPlanningShop.shopCity && (
                  <p className="mt-1 text-sm text-textSecondary">
                    {selectedPlanningShop.shopCity}
                  </p>
                )}
              </div>

              {activeAssociatedShops.length > 1 && (
                <div className="w-full sm:w-auto">
                  <label
                    htmlFor="planning-shop-select"
                    className="mb-1 block text-sm font-medium text-textPrimary"
                  >
                    Planning du shop
                  </label>
                  <select
                    id="planning-shop-select"
                    value={selectedPlanningShopId}
                    onChange={(e) => setSelectedPlanningShopId(e.target.value)}
                    className="input-field w-full sm:min-w-[260px]"
                  >
                    {activeAssociatedShops.map((association) => (
                      <option key={association.shopId} value={association.shopId}>
                        {association.shopName}
                        {association.shopCity ? ` - ${association.shopCity}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <PractitionerPlanning
              value={{
                schedule:
                  practitioner.availability.shopSchedules?.[selectedPlanningShopId] || {},
              }}
              onChange={(planning) => {
                setPractitioner((prev) => {
                  if (!prev || !selectedPlanningShopId) return prev;

                  return {
                    ...prev,
                    availability: {
                      ...prev.availability,
                      shopSchedules: {
                        ...prev.availability.shopSchedules,
                        [selectedPlanningShopId]: planning.schedule,
                      },
                    },
                  };
                });

                setHasUnsavedChanges(true);
              }}
            />
          </div>
        ) : (
          <div className="card">
            <div className="py-6 text-center text-gray-500">
              <Store className="mx-auto mb-3 h-10 w-10 text-gray-400" />
              <p className="font-medium">Aucun planning disponible</p>
              <p className="mt-1 text-sm">
                Associez d'abord ce praticien a un shop actif pour definir son planning.
              </p>
            </div>
          </div>
        )}
        {/* Shop Associations */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Shops Associés</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowAddShopModal(true)}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un shop</span>
            </button>
          </div>

          {associatedShops.length > 0 ? (
            <div className="space-y-4">
              {associatedShops.map((association) => (
                <div key={association.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-textPrimary">{association.shopName}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          association.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {association.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {association.relationshipType === 'FREELANCE' && 'Freelance'}
                          {association.relationshipType === 'EMPLOYEE' && 'Employé'}
                          {association.relationshipType === 'RENTAL' && 'Location'}
                          {association.relationshipType === 'PARTNERSHIP' && 'Partenariat'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{association.shopCity}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {association.hourlyRate && (
                          <div>
                            <span className="text-gray-500">Tarif horaire:</span>
                            <span className="ml-1 font-medium">{association.hourlyRate}€/h</span>
                          </div>
                        )}
                        {association.commissionRate && (
                          <div>
                            <span className="text-gray-500">Commission:</span>
                            <span className="ml-1 font-medium">{association.commissionRate}%</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">Depuis le:</span>
                          <span className="ml-1 font-medium">
                            {new Date(association.startDate).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        {association.endDate && (
                          <div>
                            <span className="text-gray-500">Jusqu'au:</span>
                            <span className="ml-1 font-medium">
                              {new Date(association.endDate).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {association.isActive ? (
                    <button
                      type="button"
                      onClick={() => handleAskRemoveShopAssociation(association)}
                      className="text-red-600 hover:text-red-700 p-1 ml-4"
                      title="Désactiver ce shop"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleReactivateShopAssociation(association)}
                      className="text-green-600 hover:text-green-700 p-1 ml-4"
                      title="Réactiver ce shop"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Aucun shop associé</p>
              <p className="text-sm">Cliquez sur "Ajouter un shop" pour commencer</p>
            </div>
          )}
        </div>

        {/* Service Associations */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <Palette className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Services Proposés</h2>
          </div>

          {serviceOfferings.length > 0 ? (
            <div className="space-y-6">
              {/* Group by service category */}
              {['TATTOO', 'PIERCING', 'CONSULTATION', 'AFTERCARE', 'REMOVAL', 'JEWELRY'].map(category => {
                const categoryServices = serviceOfferings.filter(service => 
                  service.category === category
                );
                
                if (categoryServices.length === 0) return null;
                
                return (
                  <div key={category} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-textPrimary mb-4">
                      {category === 'TATTOO' ? 'Tatouage' : 
                       category === 'PIERCING' ? 'Piercing' :
                       category === 'CONSULTATION' ? 'Consultation' :
                       category === 'AFTERCARE' ? 'Soins' :
                       category === 'REMOVAL' ? 'Suppression' :
                       category === 'JEWELRY' ? 'Bijouterie' : category}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {categoryServices.map((service) => (
                        <div key={service.id} className="border border-gray-100 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{service.name}</h4>
                              {service.description && (
                                <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                              )}
                              <p className="text-sm text-gray-600 mt-1">Prix de base: {service.basePrice}€</p>
                              <p className="text-xs text-gray-500">Durée: {service.estimatedDuration} min</p>
                            </div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={service.isOffered}
                                onChange={(e) => handleServiceChange(service.id, 'isOffered', e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Proposé</span>
                            </label>
                          </div>
                          
                          {service.isOffered && (
                            <div className="space-y-3 border-t pt-3">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={service.isAvailable}
                                  onChange={(e) => handleServiceChange(service.id, 'isAvailable', e.target.checked)}
                                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">Disponible</span>
                              </label>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Prix personnalisé (€)
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="5"
                                  value={service.customPrice || ''}
                                  onChange={(e) => handleServiceChange(service.id, 'customPrice', e.target.value ? parseFloat(e.target.value) : null)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder={`${service.basePrice}`}
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Notes spéciales
                                </label>
                                <input
                                  type="text"
                                  value={service.specialNotes || ''}
                                  onChange={(e) => handleServiceChange(service.id, 'specialNotes', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="Notes particulières..."
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Chargement des services...</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Shop Modal */}
      {showAddShopModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-textPrimary">Ajouter un Shop</h3>
              <button
                onClick={() => {
                  setShowAddShopModal(false);
                  setSelectedShopId('');
                  setNewAssociation({
                    relationshipType: 'FREELANCE',
                    hourlyRate: '',
                    commissionRate: '',
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Shop *
                </label>
                <select
                  value={selectedShopId}
                  onChange={(e) => setSelectedShopId(e.target.value)}
                  className="input-field"
                >
                  <option value="">Sélectionner un shop</option>
                  {shops
                    .filter(shop => !associatedShops.some(as => as.shopId === shop.id))
                    .map(shop => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name} - {shop.address?.city || ''}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Type de relation *
                </label>
                <select
                  value={newAssociation.relationshipType}
                  onChange={(e) => setNewAssociation(prev => ({
                    ...prev,
                    relationshipType: e.target.value as any
                  }))}
                  className="input-field"
                >
                  <option value="FREELANCE">Freelance</option>
                  <option value="EMPLOYEE">Employé</option>
                  <option value="RENTAL">Location</option>
                  <option value="PARTNERSHIP">Partenariat</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Tarif horaire spécifique (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="5"
                  value={newAssociation.hourlyRate}
                  onChange={(e) => setNewAssociation(prev => ({
                    ...prev,
                    hourlyRate: e.target.value
                  }))}
                  className="input-field"
                  placeholder="Optionnel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Taux de commission (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={newAssociation.commissionRate}
                  onChange={(e) => setNewAssociation(prev => ({
                    ...prev,
                    commissionRate: e.target.value
                  }))}
                  className="input-field"
                  placeholder="Optionnel"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddShopModal(false);
                  setSelectedShopId('');
                  setNewAssociation({
                    relationshipType: 'FREELANCE',
                    hourlyRate: '',
                    commissionRate: '',
                  });
                }}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleAddShopAssociation}
                disabled={!selectedShopId}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button (Mobile) */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>

      {/* Confirm Invitation Modal */}
      {practitioner && invitationModal && (
        <ConfirmInvitationModal
          isOpen={!!invitationModal}
          onClose={closeInvitationModal}
          onConfirm={handleInvitationConfirm}
          practitionerName={`${practitioner.firstName} ${practitioner.lastName}`}
          practitionerEmail={practitioner.email}
          isLoading={invitationSending}
          isLinkAccount={invitationModal === 'link'}
          error={invitationError}
        />
      )}

      {shopToRemove && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Retirer ce shop ?
            </h3>

            <p className="mt-2 text-sm leading-6 text-gray-600">
              Êtes-vous sûr de vouloir retirer
              <span className="font-semibold text-gray-900">
                {' '}{shopToRemove.shopName}{' '}
              </span>
              de cet artiste ?
            </p>

            <div className="mt-4 rounded-lg bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700">
                L'association avec ce shop sera désactivée.
                Vous pourrez la réactiver ultérieurement.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 rounded-b-2xl">
        <button
          type="button"
          onClick={() => setShopToRemove(null)}
          className="btn-secondary"
        >
          Annuler
        </button>

        <button
          type="button"
          onClick={handleConfirmRemoveShopAssociation}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
        >
          <Trash2 className="h-4 w-4" />
          Retirer le shop
        </button>
      </div>
    </div>
  </div>
)}

      {/* Success Modal */}
      {successModal && (
        <SuccessModal
          isOpen={!!successModal}
          onClose={() => setSuccessModal(null)}
          title={successModal.title}
          message={successModal.message}
        />
      )}
    </div>
  );
}
